import UserGuide from '@/components/help/UserGuide';
import { Select } from '@/components/ui/FormControls';
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Link } from 'react-router-dom';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { useChangeSaleStatus } from '@/hooks/useSales';
import { allowedSaleTransitions, saleStatusLabel } from '@/lib/saleStatus';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface Props {
  sale: {
    id: string;
    sale_number: string;
    sale_status: string;
    payment_status: string;
    return_status?: string | null;
    total_amount: number | string;
    balance_due?: number | string | null;
    paid_amount?: number | string | null;
    is_voided?: boolean;
  };
  canChangeStatus: boolean;
  onStatusUpdated?: () => void;
}

export default function SaleStatusMultiSelectPopover({
  sale,
  canChangeStatus,
  onStatusUpdated,
}: Props) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(sale.sale_status);
  const [error, setError] = useState('');
  const change = useChangeSaleStatus();
  const { role } = useAuth();
  const choices = [sale.sale_status, ...allowedSaleTransitions(sale.sale_status, role)];
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (change.isPending) return;
        setOpen(next);
        setStatus(sale.sale_status);
        setError('');
      }}
    >
      <Dialog.Trigger
        disabled={!canChangeStatus || sale.is_voided}
        className="inline-flex min-h-10 items-center gap-2 rounded-md border px-3 text-sm disabled:opacity-60"
      >
        {saleStatusLabel(sale.sale_status)}
        <ChevronDown className="h-3.5 w-3.5" />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" data-field-guide-scope />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border bg-card p-5 shadow-xl">
          <div className="flex items-start justify-between gap-3 pr-8">
            <Dialog.Title className="pr-10 text-lg font-semibold">
              Update {sale.sale_number}
            </Dialog.Title>
            <UserGuide compact />
          </div>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            Choose the fulfillment stage. Stock is adjusted when you save.
          </Dialog.Description>
          <Dialog.Close
            aria-label="Close status dialog"
            disabled={change.isPending}
            className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-md hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </Dialog.Close>
          <label htmlFor={`status-${sale.id}`} className="mb-2 mt-5 block text-sm font-medium">
            Fulfillment status
          </label>
          <Select
            id={`status-${sale.id}`}
            disabled={change.isPending}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-11 w-full rounded-md border bg-background px-3 text-sm"
            helpLabel="Fulfillment status"
          >
            {choices.map((choice) => (
              <option key={choice} value={choice}>
                {saleStatusLabel(choice)}
              </option>
            ))}
          </Select>
          {status === 'cancelled' && (
            <p className="mt-2 text-sm text-destructive">
              Cancellation releases reserved stock. Recorded collections remain in payment history;
              manage any refund separately.
            </p>
          )}
          <div className="mt-4 space-y-2 rounded-lg bg-muted/40 p-3 text-sm">
            <p>
              Payment: <strong>{saleStatusLabel(sale.payment_status)}</strong>
            </p>
            <p className="text-muted-foreground">
              Payment status follows recorded collections and refunds.
            </p>
            <Link
              className="inline-flex min-h-10 items-center font-medium text-primary underline"
              to={`/sales/${sale.id}/edit#payments`}
              onClick={() => setOpen(false)}
            >
              Manage payments
            </Link>
            <Link
              className="flex min-h-10 items-center font-medium text-primary underline"
              to={`/sales/${sale.id}`}
              onClick={() => setOpen(false)}
            >
              Open sale and return workflow
            </Link>
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {error}
            </p>
          )}
          {error.includes('from Delivery') && (
            <Link
              className="mt-2 inline-flex min-h-11 items-center text-sm text-primary underline"
              to={`/deliveries/${sale.id}`}
            >
              Open this delivery
            </Link>
          )}
          <button
            type="button"
            disabled={change.isPending || status === sale.sale_status}
            className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
            onClick={async () => {
              try {
                await change.mutateAsync({ sale_id: sale.id, new_status: status });
                toast.success('Fulfillment updated');
                setOpen(false);
                onStatusUpdated?.();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Unable to update this sale');
              }
            }}
          >
            {change.isPending && <Loader2 className="h-4 w-4 animate-spin" />}Save fulfillment
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
